import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import AsyncSelect from 'react-select/async';
import DynamicIcon from "../DynamicIcon";
import LoadingSection from "../LoadingSection";
import CategoryTreeSelect from "./CategoryTreeSelect";
import MediaGallery from "./MediaGallery";
import {
    createProduct,
    updateProduct,
    getProductDetails,
    selectProductDetails,
    selectProductDetailsLoading,
    selectProductCreateLoading,
    selectProductCreateError,
    selectProductUpdateLoading,
    selectProductUpdateError
} from "../../redux/products/productsSlice";
import {
    closeModal,
    setHasToRefresh,
    selectRightModalId
} from "../../redux/rightModalSlice";
import { selectAccountData } from "../../redux/main/accountSlice";
import {
    getProductSettings,
    selectProductSettings
} from "../../redux/productSettings/productSettingsSlice";

const inputClass = "w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500";

export default function AddEditProduct({ mode }) {
    const dispatch = useDispatch();
    const accountData = useSelector(selectAccountData);
    const productDetails = useSelector(selectProductDetails);
    const detailsLoading = useSelector(selectProductDetailsLoading);
    const createLoading = useSelector(selectProductCreateLoading);
    const createError = useSelector(selectProductCreateError);
    const updateLoading = useSelector(selectProductUpdateLoading);
    const updateError = useSelector(selectProductUpdateError);
    const rightModalId = useSelector(selectRightModalId);
    const productSettings = useSelector(selectProductSettings);
    const token = localStorage.getItem("installamelo-auth-token") ?? "";

    // Stato del prodotto (struttura piatta)
    const [formData, setFormData] = useState({
        title: "",
        description: "",
        code: "",
        active: true,
        // Fornitore
        supplierId: "",
        supplierLabel: "",
        supplierCode: "",
        // Codici
        serialNumber: "",
        batchNumber: "",
        expiryDate: "",
        // Commerciale
        purchasePrice: 0,
        markup: 0,
        sellPrice: 0,
        listPrice: 0,
        vatId: "",
        vatValue: "",
        vatLabel: "",
        unitaId: "",
        unitaLabel: "",
        // Magazzino
        quantity: 0,
        warehouseId: "",
        warehousePositionId: "",
        warehousePositionLabel: ""
    });

    // Categorie multiple
    const [selectedCategories, setSelectedCategories] = useState([]);

    // Accessori associati
    const [linkedAccessories, setLinkedAccessories] = useState([]);
    const [showAccessoryCategoryPanel, setShowAccessoryCategoryPanel] = useState(false);
    const [showAccessoryProductPanel, setShowAccessoryProductPanel] = useState(false);

    // Media
    const [mediaList, setMediaList] = useState([]);

    // Errore di validazione
    const [validationError, setValidationError] = useState("");

    // Magazzini e posizioni
    const [warehouseOptions, setWarehouseOptions] = useState([]);
    const [positionOptions, setPositionOptions] = useState([]);
    const [warehousesLoading, setWarehousesLoading] = useState(false);

    // Caricamento impostazioni
    useEffect(() => {
        if (accountData?._id) {
            dispatch(getProductSettings({ accountId: accountData._id, token }));
        }
    }, [accountData, dispatch, token]);

    // Default in modalita' creazione: IVA 22%, unita' Pezzi
    useEffect(() => {
        if (mode === "add" && productSettings) {
            const updates = {};

            // Default unita' di acquisto: cerca "pz" o "pezzi"
            if (!formData.unitaId) {
                const units = productSettings
                    ?.filter(s => s.key === "purchaseUnits")
                    .flatMap(s => s.values || [])
                    .filter(v => v.active !== false) || [];
                const pzUnit = units.find(u => u.label && (u.label.toLowerCase() === "pz" || u.label.toLowerCase() === "pezzi"));
                if (pzUnit) {
                    updates.unitaId = pzUnit._id;
                    updates.unitaLabel = pzUnit.label;
                }
            }

            // Default IVA: cerca aliquota 22%
            if (!formData.vatId) {
                const vats = productSettings
                    ?.filter(s => s.key === "vatRates")
                    .flatMap(s => s.values || [])
                    .filter(v => v.active !== false) || [];
                const vat22 = vats.find(v => v.value === 22 || v.value === "22");
                if (vat22) {
                    updates.vatId = vat22._id;
                    updates.vatValue = vat22.value;
                    updates.vatLabel = vat22.label;
                }
            }

            if (Object.keys(updates).length > 0) {
                setFormData(prev => ({ ...prev, ...updates }));
            }
        }
    }, [mode, productSettings]);

    // Caricamento magazzini
    const loadWarehouses = useCallback(async () => {
        if (!accountData?._id || !token) return;
        setWarehousesLoading(true);
        try {
            const res = await fetch(
                `${import.meta.env.VITE_API_BASE_URL}/products/warehousesList?accountId=${accountData._id}`,
                { headers: { 'auth-token': token } }
            );
            const data = await res.json();
            const list = Array.isArray(data) ? data : (data.data || []);
            setWarehouseOptions(list);
        } catch { /* ignore */ }
        finally { setWarehousesLoading(false); }
    }, [accountData, token]);

    useEffect(() => {
        loadWarehouses();
    }, [loadWarehouses]);

    // Auto-selezione magazzino se ce n'e' uno solo (solo in creazione)
    useEffect(() => {
        if (mode === "add" && warehouseOptions.length === 1 && !formData.warehouseId) {
            setFormData(prev => ({
                ...prev,
                warehouseId: warehouseOptions[0]._id
            }));
        }
    }, [mode, warehouseOptions]);

    // Aggiorna le posizioni quando cambia il magazzino selezionato
    useEffect(() => {
        if (formData.warehouseId) {
            const wh = warehouseOptions.find(w => w._id === formData.warehouseId);
            setPositionOptions(wh?.positions || []);
        } else {
            setPositionOptions([]);
        }
    }, [formData.warehouseId, warehouseOptions]);

    // Handler per cambio magazzino
    const handleWarehouseChange = (e) => {
        const selectedId = e.target.value;
        setFormData(prev => ({
            ...prev,
            warehouseId: selectedId,
            warehousePositionId: "",
            warehousePositionLabel: ""
        }));
    };

    // Handler per cambio posizione
    const handlePositionChange = (e) => {
        const selectedId = e.target.value;
        const selected = positionOptions.find(p => p._id === selectedId);
        setFormData(prev => ({
            ...prev,
            warehousePositionId: selectedId,
            warehousePositionLabel: selected ? selected.name || "" : ""
        }));
    };

    // Caricamento dettagli prodotto in modalita' edit
    useEffect(() => {
        if (mode === "edit" && rightModalId) {
            dispatch(getProductDetails({ productId: rightModalId, token }));
        }
    }, [mode, rightModalId, dispatch, token]);

    // Popolamento form con i dettagli del prodotto caricato
    useEffect(() => {
        if (mode === "edit" && productDetails) {
            setFormData({
                title: productDetails.title || "",
                description: productDetails.description || "",
                code: productDetails.code || "",
                active: productDetails.active !== undefined ? productDetails.active : true,
                supplierId: productDetails.supplierId || "",
                supplierLabel: productDetails.supplierLabel || "",
                supplierCode: productDetails.supplierCode || "",
                serialNumber: productDetails.serialNumber || "",
                batchNumber: productDetails.batchNumber || "",
                expiryDate: productDetails.expiryDate || "",
                purchasePrice: productDetails.purchasePrice ?? 0,
                markup: productDetails.markup ?? 0,
                sellPrice: productDetails.sellPrice ?? 0,
                listPrice: productDetails.listPrice ?? 0,
                vatId: productDetails.vatId || "",
                vatValue: productDetails.vatValue ?? "",
                vatLabel: productDetails.vatLabel || "",
                unitaId: productDetails.unitaId || "",
                unitaLabel: productDetails.unitaLabel || "",
                quantity: productDetails.quantity ?? 0,
                warehouseId: productDetails.warehouseId || "",
                warehousePositionId: productDetails.warehousePositionId || "",
                warehousePositionLabel: productDetails.warehousePositionLabel || ""
            });

            // Categorie
            if (productDetails.categories && productDetails.categories.length > 0) {
                setSelectedCategories(productDetails.categories);
            } else if (productDetails.categoryId) {
                // Retrocompatibilita con il vecchio campo singolo
                setSelectedCategories([{
                    categoryId: productDetails.categoryId,
                    categoryName: productDetails.categoryName || ""
                }]);
            }

            // Accessori associati
            if (productDetails.linkedAccessories && productDetails.linkedAccessories.length > 0) {
                setLinkedAccessories(productDetails.linkedAccessories);
            }

            // Media
            if (productDetails.media && productDetails.media.length > 0) {
                setMediaList(productDetails.media.map((m, i) => ({
                    ...m,
                    _tempId: m._id || `existing_${i}`
                })));
            }
        }
    }, [productDetails, mode]);

    // Handler generico per i campi del form
    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => {
            const updated = { ...prev, [name]: type === "checkbox" ? checked : value };
            // Ricalcola sellPrice quando cambiano purchasePrice o markup
            if (name === "purchasePrice" || name === "markup") {
                const price = parseFloat(name === "purchasePrice" ? value : updated.purchasePrice) || 0;
                const mkp = parseFloat(name === "markup" ? value : updated.markup) || 0;
                updated.sellPrice = Math.round(price * (1 + mkp / 100) * 100) / 100;
            }
            return updated;
        });
        setValidationError("");
    };

    // Handler per ricerca fornitori (AsyncSelect)
    const loadSupplierOptions = useCallback(async (inputValue) => {
        try {
            const url = `${import.meta.env.VITE_API_BASE_URL}/suppliers/getForSelect2?accountId=${accountData._id}&searchTerm=${encodeURIComponent(inputValue || '')}&page=0&pagination=20`;
            const res = await fetch(url, { headers: { 'auth-token': token } });
            const data = await res.json();
            return (data.results || []).map(s => ({
                value: s.id || s._id,
                label: s.name || s.title || s.text
            }));
        } catch {
            return [];
        }
    }, [accountData, token]);

    // Handler per cambio fornitore
    const handleSupplierChange = (selectedOption) => {
        setFormData((prev) => ({
            ...prev,
            supplierId: selectedOption ? selectedOption.value : "",
            supplierLabel: selectedOption ? selectedOption.label : ""
        }));
    };

    // Handler per cambio aliquota IVA (da impostazioni)
    const handleVatChange = (e) => {
        const selectedId = e.target.value;
        const vatSettings = productSettings
            ?.filter(s => s.key === "vatRates")
            .flatMap(s => s.values || []) || [];
        const selected = vatSettings.find(v => v._id === selectedId);
        setFormData((prev) => ({
            ...prev,
            vatId: selectedId,
            vatValue: selected ? selected.value || "" : "",
            vatLabel: selected ? selected.label || "" : ""
        }));
    };

    // Handler per cambio unita' di acquisto (da impostazioni)
    const handleUnitChange = (e) => {
        const selectedId = e.target.value;
        const unitSettings = productSettings
            ?.filter(s => s.key === "purchaseUnits")
            .flatMap(s => s.values || []) || [];
        const selected = unitSettings.find(v => v._id === selectedId);
        setFormData((prev) => ({
            ...prev,
            unitaId: selectedId,
            unitaLabel: selected ? selected.label || "" : ""
        }));
    };

    // --- ACCESSORI ASSOCIATI ---
    const handleAddAccessoryCategory = (cat) => {
        const already = linkedAccessories.some(a => a.referenceId === cat.categoryId);
        if (already) return;
        setLinkedAccessories(prev => [...prev, {
            type: 'category',
            referenceId: cat.categoryId,
            referenceLabel: cat.categoryName
        }]);
        setShowAccessoryCategoryPanel(false);
    };

    const handleAddAccessoryProduct = (option) => {
        if (!option) return;
        const pid = option.value;
        // Non auto-associare il prodotto a se stesso
        if (mode === 'edit' && productDetails?._id === pid) return;
        const already = linkedAccessories.some(a => a.referenceId === pid);
        if (already) return;
        setLinkedAccessories(prev => [...prev, {
            type: 'product',
            referenceId: pid,
            referenceLabel: option.label
        }]);
        setShowAccessoryProductPanel(false);
    };

    const handleRemoveAccessory = (index) => {
        setLinkedAccessories(prev => prev.filter((_, i) => i !== index));
    };

    // Ricerca prodotti per AsyncSelect accessori
    const loadAccessoryProductOptions = useCallback(async (inputValue) => {
        try {
            const url = `${import.meta.env.VITE_API_BASE_URL}/products/getForSelect2?accountId=${accountData._id}&searchTerm=${encodeURIComponent(inputValue || '')}&page=0&pagination=20`;
            const res = await fetch(url, { headers: { 'auth-token': token } });
            const data = await res.json();
            return (data.results || [])
                .filter(p => !(mode === 'edit' && (p.id || p._id) === productDetails?._id))
                .map(p => ({
                    value: p.id || p._id,
                    label: p.title || p.text || ''
                }));
        } catch {
            return [];
        }
    }, [accountData, token, mode, productDetails]);

    // --- SUBMIT ---
    const handleSubmit = async () => {
        if (!formData.title || formData.title.trim() === "") {
            setValidationError("Il nome e' un campo obbligatorio.");
            return;
        }

        setValidationError("");

        // Pulizia media: rimuovi _tempId
        const cleanMedia = mediaList.map(({ _tempId, ...rest }, i) => ({
            ...rest,
            order: i
        }));

        const productData = {
            ...formData,
            accountId: accountData._id,
            categories: selectedCategories,
            linkedAccessories: linkedAccessories,
            media: cleanMedia
        };

        if (mode === "add") {
            const result = await dispatch(createProduct({ product: productData, token }));
            if (!result.error) {
                dispatch(setHasToRefresh());
                dispatch(closeModal());
            }
        } else if (mode === "edit") {
            productData._id = productDetails._id;
            const result = await dispatch(updateProduct({ product: productData, token }));
            if (!result.error) {
                dispatch(setHasToRefresh());
                dispatch(closeModal());
            }
        }
    };

    // Formattazione data per input date
    const formatDateForInput = (dateStr) => {
        if (!dateStr) return "";
        try {
            return new Date(dateStr).toISOString().split("T")[0];
        } catch {
            return "";
        }
    };

    // Opzioni IVA e unita' (deduplica per _id nel caso ci siano setting duplicati nel DB)
    const dedup = (arr) => {
        const seen = new Set();
        return arr.filter(v => {
            const key = v._id || v.label;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    };

    const vatOptions = dedup(productSettings
        ?.filter(s => s.key === "vatRates")
        .flatMap(s => s.values || [])
        .filter(v => v.active !== false) || []);

    const unitOptions = dedup(productSettings
        ?.filter(s => s.key === "purchaseUnits")
        .flatMap(s => s.values || [])
        .filter(v => v.active !== false) || []);

    if (detailsLoading) {
        return <LoadingSection />;
    }

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="p-4 border-b flex justify-between items-start">
                <div>
                    <h2 className="text-xl font-semibold">
                        {mode === "add" ? "Nuovo Prodotto" : "Modifica Prodotto"}
                    </h2>
                    {mode === "edit" && productDetails?.code && (
                        <p className="text-sm text-gray-500 mt-1">Codice: {productDetails.code}</p>
                    )}
                </div>
                <button
                    type="button"
                    onClick={() => dispatch(closeModal())}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                    title="Chiudi"
                >
                    <DynamicIcon iconClass="fa-solid fa-times text-lg" />
                </button>
            </div>

            {/* Contenuto scrollabile */}
            <div className="flex-1 overflow-auto p-4 space-y-6">

                {/* Errori */}
                {validationError && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                        {validationError}
                    </div>
                )}
                {(createError || updateError) && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                        {createError || updateError}
                    </div>
                )}

                {/* SEZIONE: Dati Generali */}
                <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                        <DynamicIcon iconClass="fa-solid fa-info-circle text-blue-500" />
                        Dati Generali
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Codice (solo in edit, read-only) */}
                        {mode === "edit" && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Codice</label>
                                <input
                                    type="text"
                                    value={formData.code}
                                    className={`${inputClass} bg-gray-100 cursor-not-allowed`}
                                    readOnly
                                />
                            </div>
                        )}

                        {/* Nome */}
                        <div className={mode === "edit" ? "" : "md:col-span-2"}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Nome <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleFormChange}
                                className={inputClass}
                                required
                            />
                        </div>

                        {/* Descrizione */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleFormChange}
                                className={inputClass}
                                rows="3"
                            />
                        </div>

                        {/* Attivo */}
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                name="active"
                                checked={formData.active}
                                onChange={handleFormChange}
                                className="mr-2 h-4 w-4"
                                id="active-checkbox"
                            />
                            <label htmlFor="active-checkbox" className="text-sm font-medium text-gray-700">
                                Prodotto attivo
                            </label>
                        </div>
                    </div>
                </div>

                {/* SEZIONE: Categorie */}
                <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                        <DynamicIcon iconClass="fa-solid fa-tags text-green-500" />
                        Categorie
                    </h3>

                    {/* Chip categorie selezionate */}
                    {selectedCategories.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                            {selectedCategories.map((cat) => (
                                <span
                                    key={cat.categoryId}
                                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                                >
                                    {cat.categoryName}
                                    <button
                                        type="button"
                                        onClick={() => setSelectedCategories(prev => prev.filter(c => c.categoryId !== cat.categoryId))}
                                        className="ml-2 text-blue-400 hover:text-red-500"
                                    >
                                        <DynamicIcon iconClass="fa-solid fa-times text-xs" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}

                    <CategoryTreeSelect
                        selectedCategories={selectedCategories}
                        onChange={setSelectedCategories}
                    />
                </div>

                {/* SEZIONE: Accessori Associati */}
                <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                        <DynamicIcon iconClass="fa-solid fa-puzzle-piece text-indigo-500" />
                        Accessori Associati
                    </h3>

                    {/* Lista accessori correnti */}
                    {linkedAccessories.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                            {linkedAccessories.map((acc, index) => (
                                <span
                                    key={`${acc.referenceId}-${index}`}
                                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                                        acc.type === 'category'
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : 'bg-green-100 text-green-800'
                                    }`}
                                >
                                    <DynamicIcon
                                        iconClass={`fa-solid ${acc.type === 'category' ? 'fa-folder' : 'fa-box'} mr-1.5 text-xs`}
                                    />
                                    {acc.referenceLabel}
                                    <span className="ml-1 text-xs opacity-60">
                                        ({acc.type === 'category' ? 'categoria' : 'prodotto'})
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveAccessory(index)}
                                        className="ml-2 hover:text-red-500"
                                    >
                                        <DynamicIcon iconClass="fa-solid fa-times text-xs" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}

                    {linkedAccessories.length === 0 && !showAccessoryCategoryPanel && !showAccessoryProductPanel && (
                        <p className="text-sm text-gray-400 mb-3">Nessun accessorio associato.</p>
                    )}

                    {/* Bottoni aggiungi */}
                    <div className="flex gap-2 mb-3">
                        <button
                            type="button"
                            onClick={() => { setShowAccessoryCategoryPanel(!showAccessoryCategoryPanel); setShowAccessoryProductPanel(false); }}
                            className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm ${
                                showAccessoryCategoryPanel
                                    ? 'bg-yellow-500 text-white'
                                    : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                            }`}
                        >
                            <DynamicIcon iconClass="fa-solid fa-folder-plus mr-1.5" />
                            Aggiungi Categoria
                        </button>
                        <button
                            type="button"
                            onClick={() => { setShowAccessoryProductPanel(!showAccessoryProductPanel); setShowAccessoryCategoryPanel(false); }}
                            className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm ${
                                showAccessoryProductPanel
                                    ? 'bg-green-500 text-white'
                                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                            }`}
                        >
                            <DynamicIcon iconClass="fa-solid fa-box mr-1.5" />
                            Aggiungi Prodotto
                        </button>
                    </div>

                    {/* Panel inline: selezione categoria */}
                    {showAccessoryCategoryPanel && (
                        <div className="border border-yellow-300 rounded-lg p-3 bg-yellow-50 mb-3">
                            <p className="text-sm font-medium text-gray-700 mb-2">Seleziona una categoria da associare:</p>
                            <CategoryTreeSelect
                                selectedCategories={[]}
                                onChange={(cats) => {
                                    // Prendi l'ultima selezionata (singola selezione)
                                    if (cats.length > 0) {
                                        handleAddAccessoryCategory(cats[cats.length - 1]);
                                    }
                                }}
                            />
                        </div>
                    )}

                    {/* Panel inline: ricerca prodotto */}
                    {showAccessoryProductPanel && (
                        <div className="border border-green-300 rounded-lg p-3 bg-green-50 mb-3">
                            <p className="text-sm font-medium text-gray-700 mb-2">Cerca un prodotto da associare:</p>
                            <AsyncSelect
                                cacheOptions
                                defaultOptions
                                loadOptions={loadAccessoryProductOptions}
                                onChange={handleAddAccessoryProduct}
                                value={null}
                                isClearable
                                placeholder="Cerca prodotto..."
                                noOptionsMessage={() => "Nessun prodotto trovato"}
                                styles={{
                                    control: (base) => ({ ...base, borderColor: '#d1d5db', borderRadius: '0.375rem', minHeight: '42px' })
                                }}
                            />
                        </div>
                    )}
                </div>

                {/* SEZIONE: Fornitore e Codici */}
                <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                        <DynamicIcon iconClass="fa-solid fa-truck text-orange-500" />
                        Fornitore e Codici
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Fornitore */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Fornitore</label>
                            <AsyncSelect
                                cacheOptions
                                defaultOptions
                                loadOptions={loadSupplierOptions}
                                value={formData.supplierId ? { value: formData.supplierId, label: formData.supplierLabel } : null}
                                onChange={handleSupplierChange}
                                isClearable
                                placeholder="Cerca fornitore..."
                                noOptionsMessage={() => "Nessun fornitore trovato"}
                                styles={{
                                    control: (base) => ({ ...base, borderColor: '#d1d5db', borderRadius: '0.375rem', minHeight: '42px' })
                                }}
                            />
                        </div>

                        {/* Codice fornitore */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Codice Fornitore</label>
                            <input
                                type="text"
                                name="supplierCode"
                                value={formData.supplierCode}
                                onChange={handleFormChange}
                                className={inputClass}
                            />
                        </div>

                        {/* Matricola */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Matricola</label>
                            <input
                                type="text"
                                name="serialNumber"
                                value={formData.serialNumber}
                                onChange={handleFormChange}
                                className={inputClass}
                            />
                        </div>

                        {/* Lotto */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Lotto</label>
                            <input
                                type="text"
                                name="batchNumber"
                                value={formData.batchNumber}
                                onChange={handleFormChange}
                                className={inputClass}
                            />
                        </div>

                        {/* Data scadenza */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Data Scadenza</label>
                            <input
                                type="date"
                                name="expiryDate"
                                value={formatDateForInput(formData.expiryDate)}
                                onChange={handleFormChange}
                                className={inputClass}
                            />
                        </div>
                    </div>
                </div>

                {/* SEZIONE: Commerciale */}
                <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                        <DynamicIcon iconClass="fa-solid fa-euro-sign text-purple-500" />
                        Commerciale
                    </h3>
                    {/* Riga prezzi: Acquisto | Markup | Vendita Suggerito | IVA */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Prezzo Acquisto</label>
                            <input
                                type="number"
                                name="purchasePrice"
                                value={formData.purchasePrice}
                                onChange={handleFormChange}
                                className={inputClass}
                                min="0"
                                step="0.01"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Markup (%)</label>
                            <input
                                type="number"
                                name="markup"
                                value={formData.markup}
                                onChange={handleFormChange}
                                className={inputClass}
                                min="0"
                                step="0.1"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Prezzo Vendita</label>
                            <div className="relative">
                                <input
                                    type="number"
                                    name="sellPrice"
                                    value={formData.sellPrice}
                                    className={`${inputClass} bg-gray-50`}
                                    readOnly
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Prezzo di Listino</label>
                            <input
                                type="number"
                                name="listPrice"
                                value={formData.listPrice}
                                onChange={handleFormChange}
                                className={inputClass}
                                min="0"
                                step="0.01"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Aliquota IVA</label>
                            <select
                                name="vatId"
                                value={formData.vatId}
                                onChange={handleVatChange}
                                className={inputClass}
                            >
                                <option value="">Seleziona IVA</option>
                                {vatOptions.map((v) => (
                                    <option key={v._id} value={v._id}>
                                        {v.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {/* Unita' di acquisto */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Unita' di Acquisto</label>
                            <select
                                name="unitaId"
                                value={formData.unitaId}
                                onChange={handleUnitChange}
                                className={inputClass}
                            >
                                <option value="">Seleziona unita'</option>
                                {unitOptions.map((v) => (
                                    <option key={v._id} value={v._id}>
                                        {v.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* SEZIONE: Magazzino */}
                <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                        <DynamicIcon iconClass="fa-solid fa-warehouse text-amber-500" />
                        Magazzino
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Quantita' */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Quantita'</label>
                            <input
                                type="number"
                                name="quantity"
                                value={formData.quantity}
                                onChange={handleFormChange}
                                className={inputClass}
                                min="0"
                                step="1"
                            />
                        </div>

                        {/* Magazzino */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Magazzino</label>
                            <select
                                name="warehouseId"
                                value={formData.warehouseId}
                                onChange={handleWarehouseChange}
                                className={inputClass}
                                disabled={warehousesLoading}
                            >
                                <option value="">Seleziona magazzino</option>
                                {warehouseOptions.map((w) => (
                                    <option key={w._id} value={w._id}>
                                        {w.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Posizione */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Posizione</label>
                            <select
                                name="warehousePositionId"
                                value={formData.warehousePositionId}
                                onChange={handlePositionChange}
                                className={inputClass}
                                disabled={!formData.warehouseId}
                            >
                                <option value="">Seleziona posizione</option>
                                {positionOptions.map((p) => (
                                    <option key={p._id} value={p._id}>
                                        {p.name}{p.code ? ` (${p.code})` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* SEZIONE: Media */}
                <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                        <DynamicIcon iconClass="fa-solid fa-image text-teal-500" />
                        Media
                    </h3>
                    <MediaGallery
                        media={mediaList}
                        onChange={setMediaList}
                    />
                </div>
            </div>

            {/* Footer con bottoni azione */}
            <div className="p-4 border-t flex justify-end gap-3">
                <button
                    type="button"
                    onClick={() => dispatch(closeModal())}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm"
                >
                    Annulla
                </button>
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={createLoading || updateLoading}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm disabled:opacity-50"
                >
                    {(createLoading || updateLoading) ? (
                        <>
                            <DynamicIcon iconClass="fa-solid fa-spinner fa-spin mr-2" />
                            Salvataggio...
                        </>
                    ) : (
                        <>
                            <DynamicIcon iconClass="fa-solid fa-save mr-2" />
                            {mode === "add" ? "Crea Prodotto" : "Salva Prodotto"}
                        </>
                    )}
                </button>
            </div>
        </div>
    );
}
